// SCR-14 collection status (plan 8.4.14, 5.10): intake per path (C1 direct, C3 gateway, C4 org reports, C2 helper),
// gaps, drop reasons, unknown event names, CLI versions, old-PoC settings, storage, run units, backups, settings
// diagnostics (presence only, never values). Also the footer status line and generic job polling.
import { z } from 'zod';
import { DropReason, GapReason, RejectReason } from '../enums.js';
import { Comparison, Count, EpochMs, Id, JobHandle, PersonRef, Ymd, periodQuery } from '../common.js';
import { OrgReportChannel } from './home.js';
import { CollectionCalendar, ConnectionIssue } from './connect.js';

export const CollectionQuery = periodQuery({});

export const CollectionOverviewResponse = z.object({
  kpis: z.object({
    receivedToday: Comparison,
    storedToday: Count,
    duplicatesToday: Count,
    droppedToday: Count,
    lastReceivedAt: EpochMs.nullable(),
    connections: z.object({ ok: Count, problem: Count, idle: Count, quiet: Count }),
    gaps: z.object({ server: Count, device: Count, longestMinutes: Count }),
  }),
  c1: z.object({
    today: z.object({ received: Count, duplicates: Count, dropped: Count, stored: Count }),
    dropped30d: z.partialRecord(DropReason, Count),
    lastError: z.object({ code: z.literal('intake_failed'), updatedAt: EpochMs }).nullable().optional(),
    rejectedCounts: z.partialRecord(RejectReason, Count).optional(),
    rejected: z.array(z.object({ person: PersonRef, deviceName: z.string().nullable(), reason: RejectReason })),
    lastReceivedAt: EpochMs.nullable(),
    formats: z.array(z.string()),
    queue: z.object({ depth: Count, oldestSec: Count, limit: Count, at: EpochMs }).nullable(),
    /** "서버가 멈춘 동안의 기록은 잃을 수 있어요" — server-wide gaps in the last 30 days. */
    loss30d: z.object({ serverGaps: Count, minutes: Count }),
  }),
  c3: z.object({ ready: z.boolean().optional(), enabled: z.boolean(), calls: Count, errors: Count, errorRate: z.number().nullable(), lastAt: EpochMs.nullable(), cliViaGateway: Count, allowedRoutes: z.array(z.string()) }),
  c4: z.object({
    ready: z.boolean().optional(),
    providers: z.array(OrgReportChannel.extend({ adminKeyConfigured: z.boolean() })),
    totals: z.object({ rows: Count, days: Count, keys: Count, costMicros: z.int().nullable() }),
    keys: z.object({ adminApi: Count, manual: Count }).nullable(),
  }),
  /** PC helper (C2, P6 pilot): which devices run it and which stopped answering (with the issue key). */
  c2: z.object({
    enabled: z.boolean(),
    pilotDevices: Count,
    errorDevices: Count,
    pilots: z.array(z.object({ person: PersonRef, deviceName: z.string() })),
    notResponding: z.array(z.object({ person: PersonRef, deviceName: z.string(), issue: ConnectionIssue, lastSeenAt: EpochMs.nullable() })),
  }),
  unknownEvents: z.array(z.object({ name: z.string(), count: Count })),
  versions: z.array(
    z.object({
      tool: z.enum(['claude_code', 'codex']),
      min: z.string(),
      latest: z.string(),
      checking: z.array(z.string()),
      checkedAt: EpochMs,
      inUse: z.array(z.object({ version: z.string(), count: Count, state: z.enum(['supported', 'checking', 'old']) })),
    }),
  ),
  legacyDevices: z.array(z.object({ person: PersonRef, deviceName: z.string(), tool: z.enum(['claude_code', 'codex']) })),
  storage: z.object({ scope: z.literal('server_volume').optional(), usedGb: z.number().nonnegative(), totalGb: z.number().positive(), byKind: z.partialRecord(z.enum(['content', 'usage', 'audit', 'other']), z.number().nonnegative()) }).nullable(),
  runUnits: z.array(z.object({ unit: z.enum(['collector', 'api', 'worker']), status: z.enum(['ok', 'warn', 'down']), lastBeatAt: EpochMs.nullable() })),
  glossaryCheck: z.object({ state: z.enum(['queued', 'running', 'done', 'failed']), startedAt: EpochMs.nullable(), finishedAt: EpochMs.nullable(), dueAt: EpochMs, done: Count, total: Count, bad: Count, durationMs: Count.nullable() }).nullable().optional(),
  lastRetentionRunAt: EpochMs.nullable(),
  lastKeyRotationAt: EpochMs.nullable(),
  backup: z.object({ lastAt: EpochMs, ok: z.boolean(), sizeGb: z.number().nonnegative(), keepDays: Count }).nullable(),
  keyBackup: z.object({ lastAt: EpochMs, ok: z.boolean(), version: z.string() }).nullable(),
  /** Setting presence only (e.g. secret configured: yes/no) — values are never returned. */
  diagnostics: z.array(z.object({ key: z.string(), present: z.boolean(), ok: z.boolean() })),
});
export type CollectionOverviewResponse = z.infer<typeof CollectionOverviewResponse>;

export const CollectionDailyResponse = z.object({
  rows: z.array(z.object({ date: Ymd, received: Count, duplicates: Count, dropped: Count, stored: Count })),
  busiest: z.object({ date: Ymd, received: Count }).nullable(),
  /** This chart returns at most the latest 30 organisation calendar days. */
  truncatedTo30d: z.boolean(),
});

export const CollectionGapsResponse = z.object({
  /** Fixed latest 14-day view; revoked devices remain visible, sample devices never appear. */
  deviceDays: z.array(z.object({ deviceId: Id, deviceName: z.string(), person: PersonRef, revoked: z.boolean(), collection: CollectionCalendar })).optional(),
  gaps: z.array(
    z.object({
      from: EpochMs,
      to: EpochMs,
      ongoing: z.boolean(),
      minutes: Count,
      scope: z.enum(['server', 'device']),
      device: z.object({ person: PersonRef, deviceName: z.string() }).nullable(),
      reason: GapReason,
      /** "수집 공백 30분" alert was sent. */
      alerted: z.boolean(),
    }),
  ),
});

/** GET /api/v1/admin/jobs/:jobId — progress of a long job started by the caller (link check, org sync, retention run). */
export const JobParams = z.object({ jobId: Id });
export const JobStatusResponse = JobHandle;

/**
 * GET /api/v1/status — footer line "● 수집 서버 연결됨 · 마지막 기록 2분 전". system.view sees the server-wide last
 * receive time; everyone else sees their own last stored record.
 */
export const FooterStatusResponse = z.object({ collectorConnected: z.boolean(), lastRecordAt: EpochMs.nullable(), basis: z.enum(['server', 'mine']), serverNow: EpochMs, timezone: z.string() });

export const adminCollectionSchemas = {
  CollectionQuery,
  CollectionOverviewResponse,
  CollectionDailyResponse,
  CollectionGapsResponse,
  JobParams,
  JobStatusResponse,
  FooterStatusResponse,
} as const;
